import { useMemo, useState } from "react";
import { usePrava } from "@/api/hooks/useAuth";
import { VETA, smieCennik } from "@/lib/prava";
import { useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  useCennikFacety,
  useCennikMaterialy,
  useHromadnaZmena,
  useMaterialDetail,
  useUpdateMaterial,
  type HromadnaZmena,
  type HromadnaZmenaPayload,
} from "@/api/hooks/useCennik";
import { useOperacie } from "@/api/hooks/useOperacie";
import { extractErrorMessage } from "@/api/client";
import type { CennikZoraditPole } from "@/api/queryKeys";
import { toast } from "@/components/ui/use-toast";
import { useDebounce } from "@/lib/useDebounce";
import { formatPocet, sklonuj } from "@/lib/format";
import { MaterialEditDialog } from "@/components/cennik/MaterialEditDialog";
import { OperacieCennikSection } from "@/components/cennik/OperacieSection";
import { CennikMenu, CennikMenuMobil } from "@/components/cennik/CennikMenu";
import { CennikFiltre } from "@/components/cennik/CennikFiltre";
import { CennikTabulka } from "@/components/cennik/CennikTabulka";
import { HromadnaLista } from "@/components/cennik/HromadnaLista";
import { HromadnaUpravaDialog } from "@/components/cennik/HromadnaUpravaDialog";
import { MarzeKategoriiPanel } from "@/components/cennik/MarzeKategoriiPanel";
import { StrukturyPanel } from "@/components/cennik/StrukturyPanel";
import {
  ANY,
  MAX_VYBRANYCH,
  PODSTATNE_MENO,
  PRAZDNE_FILTRE,
  PRAZDNY_VYBER,
  TAB_LABELS,
  VELKOSTI_STRANY,
  dalsieZoradenie,
  pohladZAdresy,
  popisHromadnej,
  sekciaZAdresy,
  type CennikSidebarFiltre,
  type CennikTab,
  type DruhFilter,
  type HromadnaAkcia,
  type PohladStranky,
  type VelkostStrany,
  type VyberRiadkov,
  type Zoradenie,
} from "@/components/cennik/cennikZalozky";
import type { DruhMaterialu, Material, Sekcia } from "@/api/schemas";

/**
 * Mapovanie tab → filter params. Sety kovaní žijú len na stránke
 * Kovania — `je_set=false` na každej záložke, nech sa počty zhodujú.
 */
function tabFilterParams(
  tab: CennikTab,
  druhFilter: DruhFilter,
): { sekcia?: Sekcia; druh?: DruhMaterialu; je_set?: boolean } {
  if (tab === "operacie") {
    // Operácie majú vlastnú sekciu — materiálové filtre sa nepoužívajú.
    return {};
  }
  return {
    sekcia: tab,
    druh:
      tab === "materialy" && druhFilter !== "vsetky" ? druhFilter : undefined,
    je_set: false,
  };
}

/** Predvolený druh nového materiálu podľa záložky (a filtra druhu). */
function druhNovehoMaterialu(
  tab: CennikTab,
  druhFilter: DruhFilter,
): DruhMaterialu | undefined {
  switch (tab) {
    case "materialy":
      return druhFilter !== "vsetky" ? druhFilter : "plosny";
    case "hrany":
      return "dlzkovy";
    case "kovanie":
      return "kusovy";
    case "operacie":
    // Tovar prichádza z cenníka dodávateľa (CRC) — ručne sa nezakladá
    case "tovar":
      return undefined;
  }
}

/** Zapamätaná veľkosť strany — kto prezerá po 200, nechce to klikať zakaždým. */
const VELKOST_STRANY_KLUC = "cennik.perPage";

function nacitajVelkostStrany(): VelkostStrany {
  try {
    const n = Number(localStorage.getItem(VELKOST_STRANY_KLUC));
    return (VELKOSTI_STRANY as readonly number[]).includes(n)
      ? (n as VelkostStrany)
      : VELKOSTI_STRANY[0];
  } catch {
    return VELKOSTI_STRANY[0];
  }
}

function ulozVelkostStrany(n: VelkostStrany): void {
  try {
    localStorage.setItem(VELKOST_STRANY_KLUC, String(n));
  } catch {
    // súkromné okno / plné úložisko — veľkosť ostane len do obnovenia
  }
}

/** Nadpis pohľadov Nastavení v menu (KOLO 199). */
const NADPIS_POHLADU: Record<Exclude<PohladStranky, "cennik">, string> = {
  "nastavenia-cien": "Nastavenia cien",
  struktury: "Štruktúry",
};

/**
 * Cenník — okno B zo stránky „Okno cenníka" (KOLO 199): ľavé menu ako
 * v Zákazkách (sekcie, Treba doriešiť, Nastavenia), vpravo nadpis sekcie,
 * riadok filtrov a tabuľka. Sekcia aj pohľad sú v adrese
 * (`?sekcia=hrany`, `?pohlad=nastavenia-cien`), menu sa správa ako
 * navigácia a odkaz sa dá poslať.
 */
/** Obsah, ktorý prihlásený len vidí (KOLO 231) — polia a tlačidlá vypnuté. */
function LenNaCitanie({
  dovod,
  children,
}: {
  dovod: string | null;
  children: React.ReactNode;
}): JSX.Element {
  if (!dovod) return <>{children}</>;
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">{dovod}</p>
      <fieldset disabled className="contents">
        {children}
      </fieldset>
    </div>
  );
}

export function CennikPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const tab = sekciaZAdresy(params.get("sekcia"));
  const topTab = pohladZAdresy(params.get("pohlad"));
  // Roly (KOLO 231): tovar upravuje obchodník, ostatné sekcie rozpočtár —
  // bez práva Cenník ukazuje, ale nemení (server to odmietne tak či tak)
  const prava = usePrava();
  const jeTovarTab = tab === "tovar";
  // Ceny operácií sú cenník výroby (KOLO 239 — konštruktér ich len číta)
  const smie = smieCennik(prava, jeTovarTab);
  const dovodZamku = smie ? null : jeTovarTab ? VETA.cennikTovaru : VETA.cennikVyroby;
  const [search, setSearch] = useState("");
  const [filtre, setFiltre] = useState<CennikSidebarFiltre>(PRAZDNE_FILTRE);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<VelkostStrany>(nacitajVelkostStrany);
  const [zoradenie, setZoradenie] = useState<Zoradenie | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const obnovit = useUpdateMaterial();
  const [createOpen, setCreateOpen] = useState(false);
  const selectedDetailQuery = useMaterialDetail(selectedId ?? undefined);
  const selectedMaterial: Material | null =
    selectedDetailQuery.data?.data ?? null;

  const debouncedSearch = useDebounce(search, 300);
  const tabParams = tabFilterParams(tab, filtre.druhFilter);

  // Zoznam aj fasety z jedného objektu — počty v rozbaľovačkách majú
  // sedieť s tým, čo tabuľka ukáže (KOLO 166)
  const zakladneFiltre = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      sekcia: tabParams.sekcia,
      druh: tabParams.druh,
      je_set: tabParams.je_set,
      kategoria: filtre.kategoria !== ANY ? filtre.kategoria : undefined,
      dodavatel: filtre.dodavatel !== ANY ? filtre.dodavatel : undefined,
      vyrobca: filtre.vyrobca !== ANY ? filtre.vyrobca : undefined,
      // Štruktúra je irelevantná pre kovanie — filter sa tam neaplikuje
      bez_struktury: (tab !== "kovanie" && filtre.bezStruktury) || undefined,
      nedokoncene: filtre.nedokoncene || undefined,
      nezaradene: filtre.nezaradene || undefined,
      len_vyradene: filtre.lenVyradene || undefined,
      len_duplicity: filtre.lenDuplicity || undefined,
      skontroluj_cenu: filtre.skontrolujCenu || undefined,
      include_daex: false,
    }),
    [
      debouncedSearch, tab, tabParams.sekcia, tabParams.druh,
      tabParams.je_set, filtre,
    ],
  );
  // Triedi server celý cenník — nie len zobrazenú stranu
  const listFiltre = useMemo(
    () => ({
      ...zakladneFiltre,
      zoradit: zoradenie?.pole,
      smer: zoradenie?.smer,
      page,
      per_page: perPage,
    }),
    [zakladneFiltre, zoradenie, page, perPage],
  );

  const query = useCennikMaterialy(listFiltre, { zachovajPredosle: true });
  const jeMaterialovyTab = tab !== "operacie";
  // Na Operáciách stačia počty sekcií do menu — fasety bez filtrov sekcie
  const facetyQuery = useCennikFacety(
    jeMaterialovyTab
      ? zakladneFiltre
      : { search: debouncedSearch || undefined, je_set: false, include_daex: false },
  );
  const facety = jeMaterialovyTab ? facetyQuery.data?.data : undefined;
  const operacieQuery = useOperacie({ systemove: false });
  const poctySekcii: Partial<Record<CennikTab, number>> = {
    operacie: operacieQuery.data?.data.length,
  };
  for (const s of facetyQuery.data?.data.sekcie ?? []) {
    poctySekcii[s.hodnota as CennikTab] = s.pocet;
  }
  const defaultDruh = druhNovehoMaterialu(tab, filtre.druhFilter);

  /**
   * Záložka má vlastné filtre aj stĺpce — staré by zo zoznamu spravili
   * prázdno. „Len vyradené" platí pre celý cenník ako hľadanie (počty
   * na záložkách ho rátajú), preto ostáva. Nuluje sa ešte v renderi
   * (nie v efekte), aby neodišiel dopyt s filtrami inej sekcie — aj keď
   * sekciu zmení tlačidlo Späť v prehliadači.
   */
  const [sekciaFiltrov, setSekciaFiltrov] = useState<CennikTab>(tab);
  if (sekciaFiltrov !== tab) {
    setSekciaFiltrov(tab);
    setFiltre((f) => ({ ...PRAZDNE_FILTRE, lenVyradene: f.lenVyradene }));
    setZoradenie(null);
    setPage(1);
  }

  function zmenZalozku(t: CennikTab): void {
    const next = new URLSearchParams(params);
    if (t === "materialy") next.delete("sekcia");
    else next.set("sekcia", t);
    next.delete("pohlad");
    setParams(next, { replace: true });
  }

  function zmenZoradenie(pole: CennikZoraditPole): void {
    setZoradenie((z) => dalsieZoradenie(z, pole));
    setPage(1);
  }

  function zmenVelkostStrany(n: VelkostStrany): void {
    setPerPage(n);
    ulozVelkostStrany(n);
    setPage(1);
  }

  async function obnovPolozku(m: Material): Promise<void> {
    try {
      await obnovit.mutateAsync({ id: m.id, data: { je_aktivny: true } });
      toast({
        title: "Položka obnovená",
        description: `${m.nazov} je späť v cenníku aj vo výberoch.`,
      });
    } catch (err) {
      toast({
        title: "Položku sa nepodarilo obnoviť",
        description: extractErrorMessage(err),
        variant: "destructive",
      });
    }
  }

  function zmenFiltre(zmena: Partial<CennikSidebarFiltre>): void {
    setFiltre((f) => ({
      ...f,
      ...zmena,
      // Kategórie sú podľa druhu (Sololit nie je dĺžkový)
      ...(zmena.druhFilter !== undefined ? { kategoria: ANY } : {}),
    }));
    // Nezaradené podľa názvu — podobné položky (krytky, tyče) idú
    // za sebou a dajú sa zaškrtnúť naraz
    if (zmena.nezaradene) setZoradenie({ pole: "nazov", smer: "asc" });
    setPage(1);
  }

  const items = query.data?.data ?? [];
  const pagination = query.data?.pagination;
  const total = pagination?.total ?? 0;

  // ─── Hromadné úpravy (KOLO 166) ───
  // Výber patrí k filtrom, s ktorými vznikol: iná záložka, filter či
  // hľadanie = iné riadky, takže výber sa sám vyprázdni (bez efektu,
  // porovnaním kľúča). Strana a triedenie ho nemenia.
  const klucVyberu = JSON.stringify(zakladneFiltre);
  const [vyberStav, setVyberStav] = useState<{ kluc: string; vyber: VyberRiadkov }>(
    { kluc: "", vyber: PRAZDNY_VYBER },
  );
  const vyber = vyberStav.kluc === klucVyberu ? vyberStav.vyber : PRAZDNY_VYBER;
  const [akcia, setAkcia] = useState<HromadnaAkcia | null>(null);
  const hromadna = useHromadnaZmena();
  const pocetVybranych = vyber.rezim === "filter" ? total : vyber.ids.size;
  const celaStrana =
    items.length > 0 &&
    (vyber.rezim === "filter" || items.every((m) => vyber.ids.has(m.id)));

  function nastavVyber(v: VyberRiadkov): void {
    setVyberStav({ kluc: klucVyberu, vyber: v });
  }

  function nadLimit(): void {
    toast({
      title: `Najviac ${MAX_VYBRANYCH} zaškrtnutých položiek`,
      description: "Na viac použi „Vybrať všetkých podľa filtra“ nad tabuľkou.",
      variant: "destructive",
    });
  }

  function prepniRiadok(id: string): void {
    if (vyber.rezim !== "ids") return;
    const ids = new Set(vyber.ids);
    if (ids.has(id)) {
      ids.delete(id);
    } else if (ids.size >= MAX_VYBRANYCH) {
      nadLimit();
      return;
    } else {
      ids.add(id);
    }
    nastavVyber({ rezim: "ids", ids });
  }

  function prepniStranu(vybrat: boolean): void {
    if (vyber.rezim !== "ids") return;
    const ids = new Set(vyber.ids);
    for (const m of items) {
      if (vybrat) ids.add(m.id);
      else ids.delete(m.id);
    }
    if (ids.size > MAX_VYBRANYCH) {
      nadLimit();
      return;
    }
    nastavVyber({ rezim: "ids", ids });
  }

  async function potvrdHromadnu(zmena: Partial<HromadnaZmena>): Promise<void> {
    // Pri filtri ide ten istý objekt ako zoznam a počet, ktorý user
    // videl — keď server napočíta iný, vráti 409 a nič nezmení
    const payload: HromadnaZmenaPayload =
      vyber.rezim === "filter"
        ? { filtre: zakladneFiltre, ocakavany_pocet: total, zmena }
        : { ids: [...vyber.ids], ocakavany_pocet: vyber.ids.size, zmena };
    const { data } = await hromadna.mutateAsync(payload);
    toast({
      title:
        data.zmenene === 0
          ? "Nič sa nezmenilo"
          : `Upravené: ${formatPocet(data.zmenene)} ${sklonuj(data.zmenene, ["položka", "položky", "položiek"])}`,
      description: popisHromadnej(data) || undefined,
    });
    nastavVyber(PRAZDNY_VYBER);
    setAkcia(null);
  }

  // Pohľad v adrese (KOLO 170) — Nastavenia odkazujú rovno na marže
  // a štruktúry (`/cennik?pohlad=nastavenia-cien`)
  function setTopTab(pohlad: PohladStranky): void {
    const next = new URLSearchParams(params);
    if (pohlad === "cennik") next.delete("pohlad");
    else next.set("pohlad", pohlad);
    setParams(next, { replace: true });
  }

  const menuProps = {
    tab,
    pohlad: topTab,
    pocty: poctySekcii,
    facety,
    filtre,
    onSekcia: zmenZalozku,
    onPohlad: setTopTab,
    onZmenaFiltre: zmenFiltre,
  };
  const pocetText =
    topTab === "cennik" && tab !== "operacie" && pagination
      ? `${formatPocet(pagination.total)} ${sklonuj(pagination.total, PODSTATNE_MENO[tab])}` +
        (filtre.lenVyradene ? " · len vyradené" : "")
      : null;

  return (
    <div className="grid gap-5 md:grid-cols-[232px_minmax(0,1fr)] md:gap-6">
      <CennikMenu {...menuProps} />

      <div className="min-w-0 space-y-4">
        <CennikMenuMobil {...menuProps} />

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex min-w-0 items-baseline gap-3">
            <h1 className="truncate text-2xl">
              {topTab === "cennik" ? TAB_LABELS[tab] : NADPIS_POHLADU[topTab]}
            </h1>
            {pocetText ? (
              <span className="shrink-0 font-mono text-sm text-muted-foreground">
                {pocetText}
              </span>
            ) : null}
          </div>
          {/* Tovar (KOLO 230) prichádza z cenníka CRC — položku mimo neho
              rieši Vlastná položka v zákazke; „Nový materiál" by tu založil
              dosku do Materiálov */}
          {topTab === "cennik" && tab !== "operacie" && tab !== "tovar" && smie ? (
            <Button
              onClick={() => setCreateOpen(true)}
              className="ml-auto"
              title="Pridať nový materiál do cenníka"
            >
              <Plus className="h-4 w-4" />
              Nový materiál
            </Button>
          ) : null}
        </div>

        {topTab === "nastavenia-cien" ? (
          <MarzeKategoriiPanel />
        ) : topTab === "struktury" ? (
          /* Katalóg štruktúr patrí výrobe — obchodník ho len vidí (KOLO 231) */
          <LenNaCitanie dovod={prava.cennik_vyroby ? null : VETA.cennikVyroby}>
            <StrukturyPanel />
          </LenNaCitanie>
        ) : tab === "operacie" ? (
          /* Operácie — vlastná CRUD sekcia, bez filtrov a materiálovej tabuľky */
          <LenNaCitanie dovod={dovodZamku}>
            <OperacieCennikSection />
          </LenNaCitanie>
        ) : (
          <>
            <CennikFiltre
              tab={tab}
              facety={facety}
              filtre={filtre}
              onZmena={zmenFiltre}
              hladat={search}
              onHladat={(text) => {
                setSearch(text);
                setPage(1);
              }}
              zoradenie={zoradenie}
              onZoradenie={(z) => {
                setZoradenie(z);
                setPage(1);
              }}
              onZrusitVsetko={() => {
                setSearch("");
                setFiltre(PRAZDNE_FILTRE);
                setPage(1);
              }}
            />

            {smie ? (
            <HromadnaLista
              vyber={vyber}
              celaStrana={celaStrana}
              total={total}
              hrany={tab === "hrany"}
              lenVyradene={filtre.lenVyradene}
              onVsetkyPodlaFiltra={() => nastavVyber({ rezim: "filter" })}
              onZrusit={() => nastavVyber(PRAZDNY_VYBER)}
              onAkcia={setAkcia}
            />
            ) : (
              <p className="text-xs text-muted-foreground">{dovodZamku}</p>
            )}

            {/* Na mobile sa posúva len tabuľka, nie stránka (KOLO 199).
                Šírka tak, aby Položke ostalo ~370 px a jej päť tlačidiel
                triedenia ostalo v riadku — pri 56rem mala 112 px a lámali
                sa do stĺpca (KOLO 218) */}
            <div className="overflow-x-auto md:overflow-visible">
              <div className="min-w-[72rem] md:min-w-0">
                <CennikTabulka
                  tab={tab}
                  items={items}
                  pagination={pagination}
                  isLoading={query.isLoading}
                  obnovuje={query.isPlaceholderData}
                  chyba={query.isError ? (query.error?.message ?? "") : null}
                  onVyber={setSelectedId}
                  onStrana={setPage}
                  zoradenie={zoradenie}
                  onZoradenie={zmenZoradenie}
                  onVelkostStrany={zmenVelkostStrany}
                  onObnovit={(m) => void obnovPolozku(m)}
                  obnovujeId={obnovit.isPending ? (obnovit.variables?.id ?? null) : null}
                  vyber={
                    smie
                      ? {
                          vyber,
                          onPrepni: prepniRiadok,
                          onPrepniStranu: prepniStranu,
                        }
                      : undefined
                  }
                  lenCitanie={!smie}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Edit dialog — pre kliknutý materiál (selectedId) */}
      {selectedMaterial && selectedId ? (
        <MaterialEditDialog
          material={selectedMaterial}
          defaultDruh={defaultDruh}
          open={!!selectedId}
          onOpenChange={(o) => {
            if (!o) setSelectedId(null);
          }}
          lenCitanie={dovodZamku}
        />
      ) : null}

      {tab !== "operacie" ? (
        <HromadnaUpravaDialog
          akcia={akcia}
          pocet={pocetVybranych}
          tab={tab}
          onClose={() => setAkcia(null)}
          onPotvrdit={potvrdHromadnu}
        />
      ) : null}

      {/* Create dialog — nový materiál */}
      <MaterialEditDialog
        material={null}
        defaultDruh={defaultDruh}
        open={createOpen}
        onOpenChange={setCreateOpen}
      />
    </div>
  );
}
